import type { FillResult, RgbaTuple } from "./fillTypes";
import { fillWorkingBytes, MAX_FILL_QUEUE_ENTRIES } from "./fillMemory";

function pixelMatchesCanonicalTarget(
  data: Uint8ClampedArray,
  offset: number,
  targetRed: number,
  targetGreen: number,
  targetBlue: number,
  targetAlpha: number,
  tolerance: number,
): boolean {
  const candidateAlpha = data[offset + 3] ?? 0;
  const candidateRed = candidateAlpha === 0 ? 0 : (data[offset] ?? 0);
  const candidateGreen = candidateAlpha === 0 ? 0 : (data[offset + 1] ?? 0);
  const candidateBlue = candidateAlpha === 0 ? 0 : (data[offset + 2] ?? 0);
  return (
    Math.abs(candidateRed - targetRed) <= tolerance &&
    Math.abs(candidateGreen - targetGreen) <= tolerance &&
    Math.abs(candidateBlue - targetBlue) <= tolerance &&
    Math.abs(candidateAlpha - targetAlpha) <= tolerance
  );
}

export function pixelMatchesTarget(
  data: Uint8ClampedArray,
  offset: number,
  targetPixel: RgbaTuple,
  tolerance: number,
): boolean {
  const targetAlpha = targetPixel[3];
  return pixelMatchesCanonicalTarget(
    data,
    offset,
    targetAlpha === 0 ? 0 : targetPixel[0],
    targetAlpha === 0 ? 0 : targetPixel[1],
    targetAlpha === 0 ? 0 : targetPixel[2],
    targetAlpha,
    tolerance,
  );
}

export function sourceOverPixel(destination: RgbaTuple, source: RgbaTuple): RgbaTuple {
  const sourceAlpha = source[3];
  const destinationAlpha = destination[3];
  const alphaNumerator = sourceAlpha * 255 + destinationAlpha * (255 - sourceAlpha);
  if (alphaNumerator <= 0) return [0, 0, 0, 0];
  const sourceRed = sourceAlpha === 0 ? 0 : source[0];
  const sourceGreen = sourceAlpha === 0 ? 0 : source[1];
  const sourceBlue = sourceAlpha === 0 ? 0 : source[2];
  return [
    Math.round(
      (sourceRed * sourceAlpha * 255 + destination[0] * destinationAlpha * (255 - sourceAlpha)) /
        alphaNumerator,
    ),
    Math.round(
      (sourceGreen * sourceAlpha * 255 + destination[1] * destinationAlpha * (255 - sourceAlpha)) /
        alphaNumerator,
    ),
    Math.round(
      (sourceBlue * sourceAlpha * 255 + destination[2] * destinationAlpha * (255 - sourceAlpha)) /
        alphaNumerator,
    ),
    Math.round(alphaNumerator / 255),
  ];
}

export function applyBoundedFloodFill(
  image: ImageData,
  seedX: number,
  seedY: number,
  targetPixel: RgbaTuple,
  replacement: RgbaTuple,
  tolerance: number,
  maximumQueueEntries = MAX_FILL_QUEUE_ENTRIES,
): FillResult {
  const pixelCount = image.width * image.height;
  if (!Number.isFinite(fillWorkingBytes(pixelCount)))
    return { status: "rejected", filledPixels: 0 };
  const x = Math.floor(seedX);
  const y = Math.floor(seedY);
  if (x < 0 || y < 0 || x >= image.width || y >= image.height) {
    return { status: "noop", filledPixels: 0 };
  }
  const visited = new Uint8Array(Math.ceil(pixelCount / 8));
  const queueLimit = Math.min(pixelCount, MAX_FILL_QUEUE_ENTRIES, maximumQueueEntries);
  if (!Number.isSafeInteger(queueLimit) || queueLimit < 1) {
    return { status: "rejected", filledPixels: 0 };
  }
  const queue = new Int32Array(queueLimit);
  let head = 0;
  let tail = 0;
  let queued = 0;
  const targetAlpha = targetPixel[3];
  const targetRed = targetAlpha === 0 ? 0 : targetPixel[0];
  const targetGreen = targetAlpha === 0 ? 0 : targetPixel[1];
  const targetBlue = targetAlpha === 0 ? 0 : targetPixel[2];
  const replacementAlpha = replacement[3];
  const replacementRed = replacementAlpha === 0 ? 0 : replacement[0];
  const replacementGreen = replacementAlpha === 0 ? 0 : replacement[1];
  const replacementBlue = replacementAlpha === 0 ? 0 : replacement[2];
  const seedIndex = y * image.width + x;
  if (
    !pixelMatchesCanonicalTarget(
      image.data,
      seedIndex * 4,
      targetRed,
      targetGreen,
      targetBlue,
      targetAlpha,
      tolerance,
    )
  ) {
    return { status: "noop", filledPixels: 0 };
  }
  const seedByte = seedIndex >> 3;
  visited[seedByte] = (visited[seedByte] ?? 0) | (1 << (seedIndex & 7));
  queue[tail] = seedIndex;
  tail += 1;
  if (tail === queue.length) tail = 0;
  queued = 1;
  let filledPixels = 0;
  while (queued > 0) {
    const index = queue[head] ?? 0;
    head += 1;
    if (head === queue.length) head = 0;
    queued -= 1;
    filledPixels += 1;
    const column = index % image.width;
    let neighbour: number;
    let byte: number;
    let mask: number;

    if (column > 0) {
      neighbour = index - 1;
      byte = neighbour >> 3;
      mask = 1 << (neighbour & 7);
      if (
        ((visited[byte] ?? 0) & mask) === 0 &&
        pixelMatchesCanonicalTarget(
          image.data,
          neighbour * 4,
          targetRed,
          targetGreen,
          targetBlue,
          targetAlpha,
          tolerance,
        )
      ) {
        visited[byte] = (visited[byte] ?? 0) | mask;
        if (queued >= queue.length) return { status: "rejected", filledPixels: 0 };
        queue[tail] = neighbour;
        tail += 1;
        if (tail === queue.length) tail = 0;
        queued += 1;
      }
    }
    if (column + 1 < image.width) {
      neighbour = index + 1;
      byte = neighbour >> 3;
      mask = 1 << (neighbour & 7);
      if (
        ((visited[byte] ?? 0) & mask) === 0 &&
        pixelMatchesCanonicalTarget(
          image.data,
          neighbour * 4,
          targetRed,
          targetGreen,
          targetBlue,
          targetAlpha,
          tolerance,
        )
      ) {
        visited[byte] = (visited[byte] ?? 0) | mask;
        if (queued >= queue.length) return { status: "rejected", filledPixels: 0 };
        queue[tail] = neighbour;
        tail += 1;
        if (tail === queue.length) tail = 0;
        queued += 1;
      }
    }
    if (index >= image.width) {
      neighbour = index - image.width;
      byte = neighbour >> 3;
      mask = 1 << (neighbour & 7);
      if (
        ((visited[byte] ?? 0) & mask) === 0 &&
        pixelMatchesCanonicalTarget(
          image.data,
          neighbour * 4,
          targetRed,
          targetGreen,
          targetBlue,
          targetAlpha,
          tolerance,
        )
      ) {
        visited[byte] = (visited[byte] ?? 0) | mask;
        if (queued >= queue.length) return { status: "rejected", filledPixels: 0 };
        queue[tail] = neighbour;
        tail += 1;
        if (tail === queue.length) tail = 0;
        queued += 1;
      }
    }
    if (index + image.width < pixelCount) {
      neighbour = index + image.width;
      byte = neighbour >> 3;
      mask = 1 << (neighbour & 7);
      if (
        ((visited[byte] ?? 0) & mask) === 0 &&
        pixelMatchesCanonicalTarget(
          image.data,
          neighbour * 4,
          targetRed,
          targetGreen,
          targetBlue,
          targetAlpha,
          tolerance,
        )
      ) {
        visited[byte] = (visited[byte] ?? 0) | mask;
        if (queued >= queue.length) return { status: "rejected", filledPixels: 0 };
        queue[tail] = neighbour;
        tail += 1;
        if (tail === queue.length) tail = 0;
        queued += 1;
      }
    }
  }
  if (filledPixels === 0) return { status: "noop", filledPixels: 0 };
  let changed = false;
  for (let index = 0; index < pixelCount; index += 1) {
    if (((visited[index >> 3] ?? 0) & (1 << (index & 7))) === 0) continue;
    const offset = index * 4;
    const destinationRed = image.data[offset] ?? 0;
    const destinationGreen = image.data[offset + 1] ?? 0;
    const destinationBlue = image.data[offset + 2] ?? 0;
    const destinationAlpha = image.data[offset + 3] ?? 0;
    const alphaNumerator = replacementAlpha * 255 + destinationAlpha * (255 - replacementAlpha);
    const resultRed =
      alphaNumerator === 0
        ? 0
        : Math.round(
            (replacementRed * replacementAlpha * 255 +
              destinationRed * destinationAlpha * (255 - replacementAlpha)) /
              alphaNumerator,
          );
    const resultGreen =
      alphaNumerator === 0
        ? 0
        : Math.round(
            (replacementGreen * replacementAlpha * 255 +
              destinationGreen * destinationAlpha * (255 - replacementAlpha)) /
              alphaNumerator,
          );
    const resultBlue =
      alphaNumerator === 0
        ? 0
        : Math.round(
            (replacementBlue * replacementAlpha * 255 +
              destinationBlue * destinationAlpha * (255 - replacementAlpha)) /
              alphaNumerator,
          );
    const resultAlpha = Math.round(alphaNumerator / 255);
    if (
      resultRed !== destinationRed ||
      resultGreen !== destinationGreen ||
      resultBlue !== destinationBlue ||
      resultAlpha !== destinationAlpha
    ) {
      changed = true;
    }
    image.data[offset] = resultRed;
    image.data[offset + 1] = resultGreen;
    image.data[offset + 2] = resultBlue;
    image.data[offset + 3] = resultAlpha;
  }
  return { status: changed ? "changed" : "noop", filledPixels };
}
